import FilterPills from 'components/Table/FilterPills';
import LoadingButton from '@enerex/unified-ui-react/LoadingButton';
import TableSearch from 'components/Table/TableSearch';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Form,
  Dropdown,
} from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import { DEV_URL, rpost, rget, rput } from "../../../utils/requests";
import { trimInvalidUTCDate } from "../../../helpers/api/utils";
import Select from "components/UnifiedSelect";
import AsyncSelect from "components/UnifiedAsyncSelect";
import FormInput from "components/FormInput/index";
import Table from "@enerex/unified-ui-react/Table";
import useShowSnackbar from "../../../hooks/useShowSnackbar";
import { useSearch } from "@enerex/unified-ui-react/hooks";

const ALIAS_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
];

const ALIAS_TYPE_OPTIONS = [
  { value: "FKA", label: "FKA" },
  { value: "DBA", label: "DBA" },
];

const PAGE_SIZES = [10, 25, 50, 100];

const findOptionByValue = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

const COMPANY_PAGE_SIZE = 30;

const companySelectStyles = {
  control: (base, state) => {
    const borderColor = state.selectProps.invalid
      ? "var(--ct-danger)"
      : "var(--ct-input-border-color)";

    return {
      ...base,
      minHeight: "33px",
      borderColor,
      "&:hover": { borderColor },
    };
  },
  menu: (base) => ({ ...base, marginTop: 0, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const fetchCompanyPage = async (searchTerm, pageNumber) => {
  const res = await rpost(`${DEV_URL}/api/company/getall`, {
    PageNumber: pageNumber,
    PageSize: COMPANY_PAGE_SIZE,
    SearchTerm: searchTerm || null,
    CompanyID: null,
    ClientID: null,
    LicenseID: null,
    orderBy: null,
    StateCode: null,
    Commodity: null,
    LicenseType: null,
    ascendingOrder: true,
    LicenseStatus: null,
  });
  const json = res?.data;
  let items = [];
  if (json?.company && Array.isArray(json.company)) {
    items = json.company;
  } else if (Array.isArray(json)) {
    items = json;
  } else if (json?.data && Array.isArray(json.data)) {
    items = json.data;
  }
  return items.map((c) => ({
    value: c.id,
    label: c.legalName || "",
  }));
};

const normalizeText = (value) => (value || "").trim().toLowerCase();

const getAliasTypeBadgeVariant = (aliasType) => {
  switch (aliasType) {
    case "FKA":
      return "warning";
    case "MERGE":
    case "Brand":
      return "success";
    default:
      return "info";
  }
};

const getAliasTypeTitle = (aliasType) => {
  switch (aliasType) {
    case "FKA":
      return "Formerly Known As";
    case "DBA":
      return "Doing Business As";
    case "Brand":
      return "Brand";
    default:
      return aliasType;
  }
};

const CompanyAlias = () => {
  const snackbar = useShowSnackbar();

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const formattedDate = trimInvalidUTCDate(dateString, false);
      return formattedDate || "";
    } catch (e) {
      return "";
    }
  };

  // --- Search & Sorting ---
  const { search, setSearch } = useSearch();
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [aliasFilter, setAliasFilter] = useState("all");

  // --- Data ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  // --- Company Dropdown (lazy loaded) ---
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [draftCompany, setDraftCompany] = useState(null);
  const [draftAssignment, setDraftAssignment] = useState('all');
  const applyFilters = () => {
    setAppliedSearch(search.trim() || appliedSearch);
    setSearch('');
    setSelectedCompany(draftCompany);
    setAliasFilter(draftAssignment);
    setCurrentPage(1);
  };
  const companyPageRef = useRef(1);
  const companySearchRef = useRef("");
  const companyHasMoreRef = useRef(true);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const searchTimerRef = useRef(null);

  // --- Move modal dropdown ---
  const [moveSelectedCompany, setMoveSelectedCompany] = useState(null);
  const moveCompanySearchTimerRef = useRef(null);
  const moveCompanySearchResolveRef = useRef(null);

  // --- Modals ---
  const [deleteModal, setDeleteModal] = useState({ show: false, row: null });
  const [deletingId, setDeletingId] = useState(null);
  const [checkingMappingId, setCheckingMappingId] = useState(null);

  const [editModal, setEditModal] = useState({ show: false, row: null });
  const [editAliasName, setEditAliasName] = useState("");
  const [editAliasType, setEditAliasType] = useState("FKA");
  const [saving, setSaving] = useState(false);

  const [moveModal, setMoveModal] = useState({
    show: false,
    row: null,
    fromMappedDelete: false,
  });
  const [moving, setMoving] = useState(false);

  // --- Add Alias ---
  const [showAddAliasModal, setShowAddAliasModal] = useState(false);
  const [newAliasName, setNewAliasName] = useState("");
  const [aliasCompanyError, setAliasCompanyError] = useState("");
  const [aliasNameError, setAliasNameError] = useState("");
  const [newAliasType, setNewAliasType] = useState("FKA");
  const [addingAlias, setAddingAlias] = useState(false);
  const [addAliasCompany, setAddAliasCompany] = useState(null);
  const [addAliasOptions, setAddAliasOptions] = useState([]);
  const [addAliasLoading, setAddAliasLoading] = useState(false);
  const [duplicateAliasModal, setDuplicateAliasModal] = useState({
    show: false,
    aliasName: "",
    companies: [],
  });
  const addAliasPageRef = useRef(1);
  const addAliasSearchRef = useRef("");
  const addAliasHasMoreRef = useRef(true);
  const addAliasSearchTimerRef = useRef(null);

  // --- Company dropdown: initial load ---
  const loadInitialCompanies = useCallback(async () => {
    setCompanyLoading(true);
    try {
      const options = await fetchCompanyPage("", 1);
      companyPageRef.current = 1;
      companySearchRef.current = "";
      companyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
      setCompanyOptions(options);
    } catch (e) {
      console.error("Failed to fetch companies", e);
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialCompanies();
  }, [loadInitialCompanies]);

  // --- Company dropdown: search with debounce ---
  const handleCompanyInputChange = useCallback((inputValue) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      companySearchRef.current = inputValue;
      companyPageRef.current = 1;
      companyHasMoreRef.current = true;
      setCompanyLoading(true);
      try {
        const options = await fetchCompanyPage(inputValue, 1);
        companyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
        setCompanyOptions(options);
      } catch (e) {
        console.error("Failed to search companies", e);
      } finally {
        setCompanyLoading(false);
      }
    }, 400);
  }, []);

  // --- Company dropdown: load more on scroll to bottom ---
  const handleCompanyScrollToBottom = useCallback(async () => {
    if (!companyHasMoreRef.current || companyLoading) return;
    setCompanyLoading(true);
    try {
      const nextPage = companyPageRef.current + 1;
      const moreOptions = await fetchCompanyPage(companySearchRef.current, nextPage);
      companyPageRef.current = nextPage;
      companyHasMoreRef.current = moreOptions.length >= COMPANY_PAGE_SIZE;
      setCompanyOptions((prev) => [...prev, ...moreOptions]);
    } catch (e) {
      console.error("Failed to load more companies", e);
    } finally {
      setCompanyLoading(false);
    }
  }, [companyLoading]);

  // --- Move modal dropdown: load options ---
  const loadMoveCompanyOptions = useCallback(async (inputValue) => {
    if (moveCompanySearchTimerRef.current) {
      clearTimeout(moveCompanySearchTimerRef.current);
    }
    if (moveCompanySearchResolveRef.current) {
      moveCompanySearchResolveRef.current([]);
      moveCompanySearchResolveRef.current = null;
    }

    const currentCompanyId = moveModal.row?.companyId;

    return new Promise((resolve) => {
      moveCompanySearchResolveRef.current = resolve;
      moveCompanySearchTimerRef.current = setTimeout(async () => {
        moveCompanySearchTimerRef.current = null;
        moveCompanySearchResolveRef.current = null;

        try {
          const options = await fetchCompanyPage(inputValue, 1);
          resolve(options.filter((o) => o.value !== currentCompanyId));
        } catch (e) {
          console.error("Failed to fetch companies for move", e);
          resolve([]);
        }
      }, 400);
    });
  }, [moveModal.row?.companyId]);

  // --- Add Alias modal dropdown: initial load ---
  const loadAddAliasCompanies = useCallback(async () => {
    setAddAliasLoading(true);
    try {
      const options = await fetchCompanyPage("", 1);
      addAliasPageRef.current = 1;
      addAliasSearchRef.current = "";
      addAliasHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
      setAddAliasOptions(options);
    } catch (e) {
      console.error("Failed to fetch companies for add alias", e);
    } finally {
      setAddAliasLoading(false);
    }
  }, []);

  // --- Add Alias modal dropdown: search with debounce ---
  const handleAddAliasInputChange = useCallback((inputValue) => {
    if (addAliasSearchTimerRef.current) clearTimeout(addAliasSearchTimerRef.current);
    addAliasSearchTimerRef.current = setTimeout(async () => {
      addAliasSearchRef.current = inputValue;
      addAliasPageRef.current = 1;
      addAliasHasMoreRef.current = true;
      setAddAliasLoading(true);
      try {
        const options = await fetchCompanyPage(inputValue, 1);
        addAliasHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
        setAddAliasOptions(options);
      } catch (e) {
        console.error("Failed to search companies for add alias", e);
      } finally {
        setAddAliasLoading(false);
      }
    }, 400);
  }, []);

  // --- Add Alias modal dropdown: load more on scroll to bottom ---
  const handleAddAliasScrollToBottom = useCallback(async () => {
    if (!addAliasHasMoreRef.current || addAliasLoading) return;
    setAddAliasLoading(true);
    try {
      const nextPage = addAliasPageRef.current + 1;
      const moreOptions = await fetchCompanyPage(addAliasSearchRef.current, nextPage);
      addAliasPageRef.current = nextPage;
      addAliasHasMoreRef.current = moreOptions.length >= COMPANY_PAGE_SIZE;
      setAddAliasOptions((prev) => [...prev, ...moreOptions]);
    } catch (e) {
      console.error("Failed to load more companies for add alias", e);
    } finally {
      setAddAliasLoading(false);
    }
  }, [addAliasLoading]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      if (addAliasSearchTimerRef.current) {
        clearTimeout(addAliasSearchTimerRef.current);
        addAliasSearchTimerRef.current = null;
      }
      if (moveCompanySearchTimerRef.current) {
        clearTimeout(moveCompanySearchTimerRef.current);
        moveCompanySearchTimerRef.current = null;
      }
      if (moveCompanySearchResolveRef.current) {
        moveCompanySearchResolveRef.current([]);
        moveCompanySearchResolveRef.current = null;
      }
    };
  }, []);

  // --- Fetch Alias Data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rpost(`${DEV_URL}/api/company/GetAlias`, {
        PageNumber: currentPage,
        PageSize: pageSize,
        SearchTerm: appliedSearch || null,
        CompanyID: selectedCompany ? selectedCompany.value : null,
        ClientID: null,
        LicenseID: null,
        orderBy: sortColumn || "aliasName",
        StateCode: null,
        Commodity: null,
        LicenseType: null,
        ascendingOrder: sortDirection === "asc",
        HasCompany: aliasFilter === "assigned" ? 1 : aliasFilter === "unassigned" ? 0 : null,
      });
      const json = res?.data;
      let aliases = [];
      let total = 0;
      if (json?.aliases && Array.isArray(json.aliases)) {
        aliases = json.aliases;
        total = json.totalRecords || json.totalCount || json.total || aliases.length;
      } else if (Array.isArray(json)) {
        aliases = json;
        total = aliases.length;
      } else if (json?.data && Array.isArray(json.data)) {
        aliases = json.data;
        total = json.totalRecords || json.totalCount || json.total || aliases.length;
      }
      setData(
        aliases.map((item) => ({
          id: item.id,
          aliasName: item.aliasName || "",
          aliasType: item.aliasType || "",
          companyId: item.companyId || item.CompanyId || (selectedCompany ? selectedCompany.value : ""),
          companyName: item.companyName || item.CompanyName || (selectedCompany ? selectedCompany.label : ""),
          companyDomain: item.companyDomain || "",
          isPremium: item.isPremium,
          createdBy: item.createdBy || "",
          createdDate: item.createdDate || "",
        }))
      );
      setTotalRecords(total);
    } catch (e) {
      console.error("Failed to fetch aliases", e);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, currentPage, pageSize, appliedSearch, sortColumn, sortDirection, aliasFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Delete ---
  const openDeleteConfirm = (row) => setDeleteModal({ show: true, row });
  const closeDeleteConfirm = () => setDeleteModal({ show: false, row: null });

  const getHasAliasApplicationMapping = async (aliasId) => {
    const response = await rpost(
      `${DEV_URL}/api/company/AssetApplicationMapping`,
      {
        AssetId: aliasId,
        AssetType: "Alias",
      }
    );

    if (
      !response ||
      response.status < 200 ||
      response.status >= 300 ||
      response?.data?.success === false ||
      response?.data?.Success === false
    ) {
      snackbar(
        response?.data?.message || "Unable to verify alias asset mapping",
        "error"
      );
      return null;
    }

    const resultData =
      response?.data?.data ?? response?.data?.Data ?? response?.data;
    const mappingResult = Array.isArray(resultData)
      ? resultData[0]
      : resultData;
    const hasApplicationMapping =
      mappingResult?.hasApplicationMapping ??
      mappingResult?.HasApplicationMapping ??
      false;

    return (
      hasApplicationMapping === true ||
      hasApplicationMapping === 1 ||
      String(hasApplicationMapping).toLowerCase() === "true"
    );
  };

  const handleDeleteAliasRequest = async (row) => {
    if (!row?.id) {
      snackbar("Unable to delete alias", "error");
      return;
    }

    try {
      setCheckingMappingId(row.id);
      const hasApplicationMapping = await getHasAliasApplicationMapping(row.id);

      if (hasApplicationMapping === null) {
        return;
      }

      if (hasApplicationMapping) {
        openMoveModal(row, true);
        return;
      }

      openDeleteConfirm(row);
    } catch (e) {
      console.error("Failed to check alias mapping", e);
      snackbar("Failed to check alias mapping.", "error");
    } finally {
      setCheckingMappingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    const row = deleteModal.row;
    if (!row) return;
    try {
      setDeletingId(row.id);
      await rget(`${DEV_URL}/api/company/RemoveAlias/${row.id}`);
      await fetchData();
      closeDeleteConfirm();
      snackbar("Alias deleted successfully", "success");
    } catch (e) {
      console.error("Failed to delete alias", e);
      snackbar("Failed to delete alias.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  // --- Edit Alias ---
  const openEditModal = (row) => {
    setEditAliasName(row.aliasName);
    setEditAliasType(row.aliasType || "FKA");
    setEditModal({ show: true, row });
  };
  const closeEditModal = () => {
    setEditModal({ show: false, row: null });
    setEditAliasName("");
    setEditAliasType("FKA");
  };

  const handleSaveEdit = async () => {
    const row = editModal.row;
    if (!row || !editAliasName.trim()) return;
    setSaving(true);
    try {
      await rput(`${DEV_URL}/api/company/Alias/Edit`, {
        AliasId: row.id,
        CompanyId: row.companyId,
        AliasName: editAliasName.trim(),
        AliasType: editAliasType,
      });
      await fetchData();
      closeEditModal();
      snackbar("Alias updated successfully", "success");
    } catch (e) {
      console.error("Failed to update alias", e);
      snackbar("Failed to update alias.", "error");
    } finally {
      setSaving(false);
    }
  };

  // --- Merge to Company ---
  const openMoveModal = (row, fromMappedDelete = false) => {
    setMoveSelectedCompany(null);
    setMoveModal({ show: true, row, fromMappedDelete });
  };
  const closeMoveModal = () => {
    if (moveCompanySearchTimerRef.current) {
      clearTimeout(moveCompanySearchTimerRef.current);
      moveCompanySearchTimerRef.current = null;
    }
    if (moveCompanySearchResolveRef.current) {
      moveCompanySearchResolveRef.current([]);
      moveCompanySearchResolveRef.current = null;
    }
    setMoveModal({ show: false, row: null, fromMappedDelete: false });
    setMoveSelectedCompany(null);
  };

  const handleConfirmMove = async () => {
    const row = moveModal.row;
    if (!row) return;
    if (!moveSelectedCompany) {
      snackbar("Please select a target company", "error");
      return;
    }
    setMoving(true);
    try {
      await rpost(`${DEV_URL}/api/company/Alias/Transfer`, {
        AliasId: row.id,
        CompanyId: row.companyId,
        TargetCompanyId: moveSelectedCompany.value,
        ExistingCompanyId: row.companyId,
      });
      await fetchData();
      closeMoveModal();
      snackbar("Alias transferred successfully", "success");
    } catch (e) {
      console.error("Failed to merge alias", e);
      snackbar("Failed to merge alias.", "error");
    } finally {
      setMoving(false);
    }
  };

  const getDuplicateAliasConflicts = (response, aliasName) => {
    const data = response?.data ?? response;
    const conflicts =
      data?.conflicts ??
      data?.Conflicts ??
      data?.data?.conflicts ??
      data?.data?.Conflicts ??
      [];

    if (!Array.isArray(conflicts)) {
      return [];
    }

    return conflicts
      .map((item) => ({
        companyId: item.companyId || item.CompanyId,
        companyName:
          item.companyName ||
          item.CompanyName ||
          item.legalName ||
          item.LegalName ||
          "Company",
        companyStatus: item.companyStatus || item.CompanyStatus || "",
        aliasName: item.aliasName || item.AliasName || aliasName,
        aliasType: item.aliasType || item.AliasType || "",
        enerexCompanyIdentifier:
          item.enerexCompanyIdentifier || item.EnerexCompanyIdentifier || "",
      }))
      .filter((item) => item.companyId);
  };

  const openDuplicateAliasModal = (response, aliasName) => {
    const companies = getDuplicateAliasConflicts(response, aliasName);
    if (companies.length === 0) {
      return false;
    }

    setDuplicateAliasModal({
      show: true,
      aliasName,
      companies,
    });
    return true;
  };

  const closeDuplicateAliasModal = () => {
    setDuplicateAliasModal({
      show: false,
      aliasName: "",
      companies: [],
    });
  };

  const fetchDuplicateAliasConflicts = async (aliasName) => {
    const res = await rpost(`${DEV_URL}/api/company/GetAlias`, {
      PageNumber: 1,
      PageSize: 100,
      SearchTerm: aliasName,
      CompanyID: null,
      ClientID: null,
      LicenseID: null,
      orderBy: "aliasName",
      StateCode: null,
      Commodity: null,
      LicenseType: null,
      ascendingOrder: true,
      HasCompany: null,
    });

    const json = res?.data;
    const aliases = Array.isArray(json?.aliases)
      ? json.aliases
      : Array.isArray(json?.Aliases)
        ? json.Aliases
        : Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.Data)
            ? json.Data
            : Array.isArray(json)
              ? json
              : [];

    const exactAliasName = normalizeText(aliasName);
    const uniqueConflicts = new Map();

    aliases
      .filter(
        (item) =>
          normalizeText(item.aliasName || item.AliasName) === exactAliasName
      )
      .forEach((item) => {
        const companyId = item.companyId || item.CompanyId;
        const key = `${companyId || "unassigned"}-${item.id || item.Id || ""}`;
        if (uniqueConflicts.has(key)) {
          return;
        }

        uniqueConflicts.set(key, {
          companyId,
          companyName:
            item.companyName ||
            item.CompanyName ||
            item.legalName ||
            item.LegalName ||
            "Company",
          companyStatus: item.companyStatus || item.CompanyStatus || "",
          aliasName: item.aliasName || item.AliasName || aliasName,
          aliasType: item.aliasType || item.AliasType || "",
          enerexCompanyIdentifier:
            item.enerexCompanyIdentifier || item.EnerexCompanyIdentifier || "",
        });
      });

    return Array.from(uniqueConflicts.values()).filter((item) => item.companyId);
  };

  const openDuplicateAliasConflicts = (aliasName, conflicts) =>
    openDuplicateAliasModal(
      {
        data: {
          validationType: "duplicateAlias",
          conflicts,
        },
      },
      aliasName
    );

  // --- Add Alias ---
  const handleAddAlias = async () => {
    const nextCompanyError = addAliasCompany ? "" : "Company is required.";
    const nextAliasNameError = newAliasName.trim()
      ? ""
      : "Alias Name is required.";

    setAliasCompanyError(nextCompanyError);
    setAliasNameError(nextAliasNameError);

    if (nextCompanyError || nextAliasNameError) {
      return;
    }
    setAddingAlias(true);
    try {
      const duplicateConflicts = await fetchDuplicateAliasConflicts(
        newAliasName.trim()
      );
      if (duplicateConflicts.length > 0) {
        openDuplicateAliasConflicts(newAliasName.trim(), duplicateConflicts);
        return;
      }

      const response = await rpost(
        `${DEV_URL}/api/company/${addAliasCompany.value}/Alias`,
        {
          AliasName: newAliasName.trim(),
          AliasType: newAliasType,
          AssociatedCompanyID: addAliasCompany.value,
        }
      );
      if (response?.data?.success) {
        snackbar(response.data.message || "Alias added successfully", "success");
        setShowAddAliasModal(false);
        setNewAliasName("");
        setAliasCompanyError("");
        setAliasNameError("");
        setNewAliasType("FKA");
        setAddAliasCompany(null);
        await fetchData();
      } else {
        if (openDuplicateAliasModal(response, newAliasName.trim())) {
          return;
        }
        snackbar(response?.data?.message || "Failed to add alias", "error");
      }
    } catch (e) {
      console.error("Failed to add alias", e);
      if (openDuplicateAliasModal(e?.response, newAliasName.trim())) {
        return;
      }
      snackbar(e?.response?.data?.message || "An error occurred while adding alias", "error");
    } finally {
      setAddingAlias(false);
    }
  };

  const tablePagination = {
    pageIndex: Math.max(currentPage - 1, 0),
    pageSize,
  };

  const handleTablePaginationChange = (updater) => {
    const nextPagination =
      typeof updater === "function" ? updater(tablePagination) : updater;

    if (nextPagination.pageSize !== pageSize) {
      setPageSize(nextPagination.pageSize);
      setCurrentPage(1);
      return;
    }

    setCurrentPage((nextPagination.pageIndex ?? 0) + 1);
  };

  const tableSorting = sortColumn
    ? [{ id: sortColumn, desc: sortDirection === "desc" }]
    : [];

  const handleTableSortingChange = (updater) => {
    const nextSorting =
      typeof updater === "function" ? updater(tableSorting) : updater;
    const nextSort = nextSorting?.[0];

    setSortColumn(nextSort?.id || "");
    setSortDirection(nextSort?.desc ? "desc" : "asc");
    setCurrentPage(1);
  };

  const aliasColumns = [
    {
      header: "Alias",
      accessorKey: "aliasName",
    },
    {
      header: "Type",
      accessorKey: "aliasType",
      cell: ({ row }) => (
        <span
          className={`badge bg-${getAliasTypeBadgeVariant(row.original.aliasType)}`}
          title={getAliasTypeTitle(row.original.aliasType)}
        >
          {row.original.aliasType}
        </span>
      ),
    },
    {
      header: "Company",
      accessorKey: "companyName",
    },
    {
      header: "Created By",
      accessorKey: "createdBy",
    },
    {
      header: "Created On",
      accessorKey: "createdDate",
      cell: ({ row }) => formatDate(row.original.createdDate),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;

        return (
          <Dropdown drop="down" align="end" className="position-static">
            <Dropdown.Toggle
              variant="link"
              className="btn btn-sm btn-link arrow-none p-0 shadow-none text-muted"
              style={{
                fontSize: "1.2rem",
                lineHeight: "1",
                cursor: "pointer",
              }}
            >
              &#x22EE;
            </Dropdown.Toggle>
            <Dropdown.Menu
              renderOnMount
              popperConfig={{ strategy: "fixed" }}
              style={{ zIndex: 9999, minWidth: "180px" }}
            >
              <Dropdown.Item
                onClick={() => openMoveModal(row)}
                className="d-flex align-items-center gap-2"
                style={{ cursor: "pointer", fontSize: "0.8rem" }}
              >
                <i className="uil uil-exchange"></i>
                Merge to Company
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleDeleteAliasRequest(row)}
                className="d-flex align-items-center gap-2 text-danger"
                disabled={
                  deletingId === row.id ||
                  checkingMappingId === row.id
                }
                style={{
                  cursor:
                    deletingId === row.id ||
                    checkingMappingId === row.id
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "0.8rem",
                }}
              >
                <i className="uil uil-trash"></i>
                {checkingMappingId === row.id
                  ? "Checking..."
                  : deletingId === row.id
                    ? "Deleting..."
                    : "Delete"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      enableSorting: false,
    },
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={deleteModal.show}
        onHide={closeDeleteConfirm}
        title="Delete Alias"
        confirmButtonLabel="Delete"
        confirmButtonVariant="danger"
        isLoading={!!deletingId}
        onConfirm={handleConfirmDelete}
      >
        <p>
          Are you sure you want to delete the alias{" "}
          <strong>{deleteModal.row?.aliasName}</strong>?
        </p>
      </ConfirmationModal>

      {/* Edit Alias Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={editModal.show}
        onHide={closeEditModal}
        title="Edit Alias"
        confirmButtonLabel={saving ? "Saving..." : "Save"}
        confirmButtonVariant="primary"
        isLoading={saving}
        onConfirm={() => {
          if (!editAliasName.trim()) return;
          handleSaveEdit();
        }}
      >
        <FormInput
          type="text"
          label="Alias Name"
          value={editAliasName}
          onChange={(e) => setEditAliasName(e.target.value)}
          placeholder="Enter alias name"
        />
        <Form.Group className="mt-3">
          <Form.Label>Alias Type</Form.Label>
          <Select
            options={ALIAS_TYPE_OPTIONS}
            value={findOptionByValue(ALIAS_TYPE_OPTIONS, editAliasType)}
            onChange={(option) => setEditAliasType(option?.value || "FKA")}
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
      </ConfirmationModal>

      {/* Merge to Company Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={moveModal.show}
        onHide={closeMoveModal}
        title={moveModal.fromMappedDelete ? "Transfer Alias" : "Merge to Company"}
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={moving}
        onConfirm={handleConfirmMove}
      >
        {moveModal.fromMappedDelete ? (
          <p>
            This alias is used in another product for the same company, so you
            cannot delete it. You can either transfer it to another company, or
            first delete/correct the product data, and then delete it.
          </p>
        ) : (
          <p>
            Merging alias <strong>{moveModal.row?.aliasName}</strong> from{" "}
            <strong>{moveModal.row?.companyName}</strong>.
          </p>
        )}
        <Form.Group>
          <Form.Label>Select Target Company</Form.Label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadMoveCompanyOptions}
            value={moveSelectedCompany}
            onChange={(val) => setMoveSelectedCompany(val)}
            placeholder="Search company..."
            isClearable
            isSearchable
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            maxMenuHeight={250}
          />
        </Form.Group>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={duplicateAliasModal.show}
        onHide={closeDuplicateAliasModal}
        title="Duplicate Alias"
        confirmButtonLabel="Close"
        confirmButtonVariant="secondary"
        onConfirm={closeDuplicateAliasModal}
      >
        <p className="mb-3">
          <strong>{duplicateAliasModal.aliasName}</strong> is already present
          for the following{" "}
          {duplicateAliasModal.companies.length === 1
            ? "company"
            : "companies"}
          .
        </p>
        <div className="d-flex flex-column gap-2">
          {duplicateAliasModal.companies.map((item) => (
            <div
              key={`${item.companyId}-${item.aliasName}-${item.aliasType}`}
              className="border rounded p-2"
            >
              <a
                href={`/companies/${item.companyId}`}
                target="_blank"
                rel="noreferrer"
              >
                {item.companyName}
              </a>
              {item.companyStatus ? (
                <small className="text-muted d-block">
                  Status: {item.companyStatus}
                </small>
              ) : null}
              {item.aliasType ? (
                <small className="text-muted d-block">
                  Alias Type: {item.aliasType}
                </small>
              ) : null}
              {item.enerexCompanyIdentifier ? (
                <small className="text-muted d-block">
                  Enerex Company Id: {item.enerexCompanyIdentifier}
                </small>
              ) : null}
            </div>
          ))}
        </div>
      </ConfirmationModal>

      {/* Add Alias Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={showAddAliasModal}
        onHide={() => {
          setShowAddAliasModal(false);
          setAliasCompanyError("");
          setAliasNameError("");
        }}
        title="Add Alias"
        confirmButtonLabel={addingAlias ? "Adding..." : "Add Alias"}
        confirmButtonVariant="primary"
        isLoading={addingAlias}
        onConfirm={handleAddAlias}
      >
        <Form.Group className="mb-3">
          <Form.Label htmlFor="add-alias-company">
            Company <span className="text-danger" aria-hidden="true">*</span>
          </Form.Label>
          <Select
            inputId="add-alias-company"
            options={addAliasOptions}
            onMenuScrollToBottom={handleAddAliasScrollToBottom}
            onInputChange={handleAddAliasInputChange}
            filterOption={null}
            value={addAliasCompany}
            onChange={(val) => {
              setAddAliasCompany(val);
              if (val) setAliasCompanyError("");
            }}
            placeholder="Search company..."
            isClearable
            isSearchable
            isLoading={addAliasLoading}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            maxMenuHeight={250}
            invalid={Boolean(aliasCompanyError)}
            aria-required="true"
            aria-invalid={Boolean(aliasCompanyError)}
            aria-describedby={aliasCompanyError ? "add-alias-company-error" : undefined}
          />
          {aliasCompanyError ? (
            <Form.Control.Feedback
              type="invalid"
              className="d-block"
              id="add-alias-company-error"
              role="alert"
            >
              {aliasCompanyError}
            </Form.Control.Feedback>
          ) : null}
        </Form.Group>
        <FormInput
          type="text"
          name="newAliasName"
          errors={
            aliasNameError
              ? { newAliasName: { message: aliasNameError } }
              : undefined
          }
          label={
            <>
              Alias Name <span className="text-danger" aria-hidden="true">*</span>
            </>
          }
          required
          containerClass="mb-3"
          placeholder="Enter alias name"
          value={newAliasName}
          onChange={(e) => {
            setNewAliasName(e.target.value);
            if (e.target.value.trim()) setAliasNameError("");
          }}
        />
        <Form.Group className="mb-3">
          <Form.Label>Alias Type</Form.Label>
          <Select
            options={ALIAS_TYPE_OPTIONS}
            value={findOptionByValue(ALIAS_TYPE_OPTIONS, newAliasType)}
            onChange={(option) => setNewAliasType(option?.value || "FKA")}
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
      </ConfirmationModal>

      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <h4
          className="header-title mb-0"
          style={{ color: "#313a46", fontWeight: 600, fontSize: "1.125rem" }}
        >
        </h4>

        <div className="d-flex align-items-center gap-2">
          <LoadingButton type="button" variant="primary" size="sm" onClick={applyFilters}>Apply</LoadingButton>
          <LoadingButton
            variant="primary"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="d-flex align-items-center gap-1"
          >
            <i className="uil uil-refresh"></i> Refresh
          </LoadingButton>

          <LoadingButton
            variant="primary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => {
              setShowAddAliasModal(true);
              setNewAliasName("");
              setAliasCompanyError("");
              setAliasNameError("");
              setNewAliasType("FKA");
              setAddAliasCompany(selectedCompany || null);
              loadAddAliasCompanies();
            }}
          >
            <i className="uil uil-plus"></i> Add Alias
          </LoadingButton>
        </div>
      </div>

      <Card>
        <Card.Body style={{ overflow: "visible" }}>
          {/* Search + Company Dropdown + Filter */}
          <div className="d-flex flex-wrap align-items-start mb-2 gap-2">
            <div style={{ maxWidth: 260, flexShrink: 0 }}>
              <TableSearch value={search} onChange={setSearch} onSearch={applyFilters}
                showAction={false} showApplied={false} />
            </div>

            <div style={{ minWidth: 220, flex: 1 }}>
              <Select
                options={companyOptions}
                onMenuScrollToBottom={handleCompanyScrollToBottom}
                onInputChange={handleCompanyInputChange}
                filterOption={null}
                value={draftCompany}
                onChange={setDraftCompany}
                placeholder="Filter by company..."
                isClearable
                isSearchable
                isLoading={companyLoading}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={companySelectStyles}
                maxMenuHeight={250}
              />
            </div>

            <div style={{ width: 130, flexShrink: 0 }}>
              <Select
                value={findOptionByValue(ALIAS_FILTER_OPTIONS, draftAssignment)}
                options={ALIAS_FILTER_OPTIONS}
                onChange={option => setDraftAssignment(option?.value || 'all')}
                isSearchable={false}
                isClearable={false}
              />
            </div>

          </div>
          <FilterPills filters={[
            { label: 'Search', value: appliedSearch, onRemove: () => { setAppliedSearch(''); setCurrentPage(1); } },
            { label: 'Company', value: selectedCompany?.label, onRemove: () => { setSelectedCompany(null); setDraftCompany(null); setCurrentPage(1); } },
            { label: 'Assignment', value: aliasFilter === 'all' ? '' : findOptionByValue(ALIAS_FILTER_OPTIONS, aliasFilter)?.label,
              onRemove: () => { setAliasFilter('all'); setDraftAssignment('all'); setCurrentPage(1); } },
          ]} />

          <Table
            columns={aliasColumns}
            data={data}
            loading={loading}
            enableSearch={false}
            enablePagination
            enableHideColumns
            enableSorting
            enableFilters={false}
            enableSelection={false}
            enablePaginateAll={false}
            enableColumnVisibilityStorage={false}
            enableGlobalFilterStorage={false}
            enableUrlPagination={false}
            pageSizes={PAGE_SIZES}
            pagination={tablePagination}
            onPaginationChange={handleTablePaginationChange}
            sorting={tableSorting}
            onSortingChange={handleTableSortingChange}
            total={totalRecords}
            noDataMessage="No aliases found."
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default CompanyAlias;
